import { ReactElement } from 'react';
import { useVideoConfig } from 'remotion';

// Themes
import { employeeYearInReviewTheme } from '@/components/EmployeeYearInReview/theme';

import { ScrollingTextRow } from './ScrollingTextRow';

interface ScrollingTextBackgroundProps {
  /** Array of texts to display in alternating rows */
  texts: string[];
  /** Background color */
  backgroundColor?: string;
  /** Text color */
  textColor?: string;
  /** Font size in pixels */
  fontSize?: number;
  /** Speed of scrolling in pixels per frame */
  speed?: number;
  /** Vertical gap between rows */
  rowGap?: number;
  /** Top offset for positioning */
  topOffset?: number;
}

const {
  rawColors: { scrollingTextBackground },
} = employeeYearInReviewTheme;

/**
 * ScrollingTextBackground component
 * Renders alternating rows of customizable text
 * that scroll infinitely in opposite horizontal directions
 */
export const ScrollingTextBackground = ({
  texts = [],
  backgroundColor = scrollingTextBackground.backgroundColor,
  textColor = scrollingTextBackground.textColor,
  fontSize = 650,
  speed = 3,
  rowGap = -260,
  topOffset = 170,
}: ScrollingTextBackgroundProps): ReactElement => {
  const { height } = useVideoConfig();

  // Calculate row height including gap
  const rowHeight = fontSize + rowGap;

  // Calculate how many rows we need to cover the screen or use provided rowCount
  const rowsNeeded = Math.ceil(height / rowHeight);

  // Generate rows dynamically based on texts array
  const rows: ReactElement[] = [];

  for (let index = 0; index < rowsNeeded; index++) {
    // Cycle through the texts array
    const textIndex = index % texts.length;
    const text = texts[textIndex] ?? texts[0] ?? '';

    // Alternate direction for each row
    const direction = index % 2 === 0 ? 'left' : 'right';

    rows.push(
      <ScrollingTextRow
        key={`row-${index}`}
        text={text}
        direction={direction}
        top={index * rowHeight - rowGap + topOffset}
        speed={speed}
        fontSize={fontSize}
        color={textColor}
      />,
    );
  }

  return (
    <div
      className="absolute inset-0 overflow-hidden"
      style={{ backgroundColor }}
    >
      <div className="absolute origin-center -rotate-[5deg] w-[200%] h-[200%] -left-1/2 -top-1/2">
        {rows}
      </div>
    </div>
  );
};
